/**
 * alertsSlice — Redux state for the spread-alert subscriptions feature.
 *
 * Talks to /api/alerts/* endpoints. Mirrors the user's pairing status,
 * subscription list, and the latest pairing code (to display the /start
 * instructions while waiting for confirmation).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApiUrl, addUserParam } from '../config/api.config';

// --- helpers --------------------------------------------------------------

async function jfetch(method, path, body) {
  const url = addUserParam(`${getApiUrl()}${path}`);
  const opts = { method, credentials: 'include' };
  if (body !== undefined) {
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  const resp = await fetch(url, opts);
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(text || `Request failed: ${resp.status}`);
  }
  return resp.json();
}

// --- thunks ---------------------------------------------------------------

export const startPairing = createAsyncThunk(
  'alerts/startPairing',
  async (_, { rejectWithValue }) => {
    try { return await jfetch('POST', '/alerts/pairing/start'); }
    catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

export const fetchPairingStatus = createAsyncThunk(
  'alerts/fetchPairingStatus',
  async (_, { rejectWithValue }) => {
    try { return await jfetch('GET', '/alerts/pairing/status'); }
    catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

export const fetchSubscriptions = createAsyncThunk(
  'alerts/fetchSubscriptions',
  async (_, { rejectWithValue }) => {
    try { return await jfetch('GET', '/alerts/subscriptions'); }
    catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

export const upsertSubscription = createAsyncThunk(
  'alerts/upsertSubscription',
  async ({ ticker1, ticker2, levels, cooldownSecondsPerLevel }, { rejectWithValue }) => {
    try {
      // cooldownSecondsPerLevel is an array parallel to levels. Backend
      // clamps each entry to [0, 86400]; 0 = no cooldown for that level.
      // Missing/invalid entries default to 1800.
      return await jfetch('POST', '/alerts/subscriptions', {
        ticker1, ticker2, levels, cooldownSecondsPerLevel,
      });
    }
    catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

export const deleteSubscription = createAsyncThunk(
  'alerts/deleteSubscription',
  async (id, { rejectWithValue }) => {
    try {
      await jfetch('DELETE', `/alerts/subscriptions/${id}`);
      return { id };
    } catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

export const syncScalingFactors = createAsyncThunk(
  'alerts/syncScalingFactors',
  async (scalingFactors, { rejectWithValue }) => {
    try { return await jfetch('POST', '/alerts/sync-state', { scalingFactors }); }
    catch (e) { return rejectWithValue({ message: e.message }); }
  }
);

// --- slice ----------------------------------------------------------------

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: {
    paired: false,             // updated from /pairing/status
    pairingCode: null,         // set after startPairing, cleared on bind
    subscriptions: [],         // list of { _id, username, ticker1, ticker2, levels[], lastFiredAt, createdAt }
    loading: false,
    error: null,
  },
  reducers: {
    clearPairingCode: (state) => { state.pairingCode = null; },
  },
  extraReducers: (b) => {
    b.addCase(startPairing.fulfilled, (state, a) => {
      state.pairingCode = a.payload?.code || null;
    });
    b.addCase(fetchPairingStatus.fulfilled, (state, a) => {
      state.paired = !!a.payload?.paired;
      if (state.paired) state.pairingCode = null;  // pairing complete
    });
    b.addCase(fetchSubscriptions.fulfilled, (state, a) => {
      state.subscriptions = a.payload?.subscriptions || [];
    });
    b.addCase(upsertSubscription.fulfilled, (state, a) => {
      const sub = a.payload?.subscription;
      if (!sub) return;
      const i = state.subscriptions.findIndex((s) => s._id === sub._id);
      if (i >= 0) state.subscriptions[i] = sub;
      else state.subscriptions.push(sub);
    });
    b.addCase(deleteSubscription.fulfilled, (state, a) => {
      const id = a.payload?.id;
      state.subscriptions = state.subscriptions.filter((s) => s._id !== id);
    });
    // generic loading/error tracking
    b.addMatcher(
      (action) => action.type.startsWith('alerts/') && action.type.endsWith('/pending'),
      (state) => { state.loading = true; state.error = null; }
    );
    b.addMatcher(
      (action) => action.type.startsWith('alerts/') && action.type.endsWith('/fulfilled'),
      (state) => { state.loading = false; }
    );
    b.addMatcher(
      (action) => action.type.startsWith('alerts/') && action.type.endsWith('/rejected'),
      (state, a) => {
        state.loading = false;
        state.error = a.payload?.message || a.error?.message || 'Request failed';
      }
    );
  },
});

export const { clearPairingCode } = alertsSlice.actions;
export default alertsSlice.reducer;
